import { Button, ButtonGroup, Center, useToast } from "@chakra-ui/react";
import { useFormik } from "formik";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import * as Yup from "yup";

import { extractErrorMessage } from "@equidefi/portals";
import { SystemDownBanner } from "@equidefi/portals/components/SystemDownBanner";
import { useWorkflowRegister } from "@equidefi/portals/hooks/useAuthentication";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useCurrentUser } from "@equidefi/portals/hooks/useUsers";
import { usePasswordStrength } from "@equidefi/portals/hooks/usePasswordStrength";
import { offeringInactiveWarning, offeringIsActive } from "@equidefi/shared";
import { BannerAlert, Input, Password, Text } from "@equidefi/ui";

import { PoweredBySection } from "@/components/nav/components";
import { OfferingInfoCard } from "@/components/workflow/OfferingInfoCard";
import { WorkflowButton } from "@/components/workflow/WorkflowButton";

import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowForm from "../WorkflowForm";
import { useWorkflowContext } from "../context/WorkflowContext";
import "./auth.css";

const buildUserSchema = (validateFn) =>
  Yup.object().shape({
    email: Yup.string().nullable().email("Must be a valid email").required("Please enter your email address"),
    password: Yup.string()
      .required("Please enter a password")
      .test(function (password = "") {
        const result = validateFn(password);
        return result === true
          ? true
          : this.createError({
              message: result,
              path: "password",
            });
      }),
  });

const initialValues = {
  email: "",
  password: "",
};

const Register = () => {
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const { refetch: refetchUser } = useCurrentUser();

  const { offering } = useWorkflowContext();
  const workflowRegister = useWorkflowRegister(offering?.id);
  const { score, analyze, validate } = usePasswordStrength();

  const navigate = useNavigate();
  const toast = useToast();
  useDocumentTitle("Register");

  const onSubmit = async (data) => {
    setIsLoading(true);
    const time = new Date();

    try {
      await workflowRegister.mutateAsync({
        ...data,
        accept_tos: time,
        accept_privacy: time,
        type: "investor",
      });
      refetchUser();

      navigate(`/offerings/${offering.slug}/mfa`, {
        state: { ...data, offering_id: offering.id },
      });
    } catch (error) {
      console.error(error);
      toast({
        status: "error",
        description: extractErrorMessage(error, "There was an error trying to register your account."),
      });
    }

    setIsLoading(false);
  };

  const { handleSubmit, getFieldProps, setFieldValue, errors, isValid, touched, values } = useFormik({
    initialValues: location?.state?.initialValues || initialValues,
    enableReinitialize: true,
    validationSchema: buildUserSchema(validate),
    onSubmit,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
  });

  const isOfferingNotActive = offering?.status && !offeringIsActive(offering);
  const inactiveWarning = offeringInactiveWarning(offering);

  return (
    <WorkflowContainer title={`Welcome to ${offering.issuer.name}'s offering!`}>
      <SystemDownBanner />
      <OfferingInfoCard offering={offering} />

      <Text mb="0">Thank you for your interest in {offering.issuer.name}.</Text>
      <Text mb={4}>Register or Log In to start your investment.</Text>

      {inactiveWarning && <BannerAlert status="warning" mb={4} description={inactiveWarning} />}

      <ButtonGroup w="full" variant="solid" mb="4">
        <Button
          w="full"
          backgroundColor="purple.100"
          boxShadow="card"
          border="1px solid"
          borderColor="purple.500"
          color="black"
          fontWeight="bold"
          minH={{ base: "48px", md: "inherit" }}
          _hover={{}}
          _active={{}}
          cursor="none">
          REGISTER
        </Button>
        <Button
          w="full"
          as={Link}
          to={"../login"}
          state={{ initialValues: { ...values, password: "" } }}
          boxShadow="card"
          colorScheme="gray"
          bgColor="white"
          minH={{ base: "48px", md: "inherit" }}
          color="black">
          LOG IN
        </Button>
      </ButtonGroup>

      <WorkflowForm onSubmit={handleSubmit}>
        <Input
          label="Email Address"
          error={errors.email}
          isInvalid={errors.email && touched.email}
          isRequired
          isDisabled={isOfferingNotActive}
          mb={4}
          {...getFieldProps("email")}
        />
        <Password
          label="Create a Password"
          error={errors.password}
          isInvalid={errors.password && touched.password}
          isRequired
          isDisabled={isOfferingNotActive}
          borderRadius={5}
          strength={score}
          {...getFieldProps("password")}
          onChange={(value) => {
            analyze(value.target.value);
            setFieldValue("password", value.target.value);
          }}
        />

        <Text textStyle="context" mb={4}>
          By creating an account you are agreeing to the{" "}
          <Text
            textStyle="context"
            as={Link}
            _hover={{ color: "blue.700" }}
            color="blue.600"
            to="https://equidefi.com/terms-of-service/"
            target="_blank">
            Terms of Service
          </Text>{" "}
          and{" "}
          <Text
            textStyle="context"
            as={Link}
            _hover={{ color: "blue.700" }}
            color="blue.600"
            to="https://equidefi.com/privacy-policy"
            target="_blank">
            Privacy Policy
          </Text>{" "}
          and consenting to receive email or SMS messages regarding your investment and other relevant offers.
        </Text>
        <WorkflowButton isDisabled={!isValid || isOfferingNotActive} isLoading={isLoading} onClick={handleSubmit}>
          Create Your Account
        </WorkflowButton>
        <Center mt="4">
          <Text as={Link} _hover={{ color: "blue.700" }} color="equidefi.blue" fontSize="md" to="/support">
            Contact Support
          </Text>
        </Center>
      </WorkflowForm>
      <PoweredBySection variant="light" />
    </WorkflowContainer>
  );
};

export default Register;
